import { useCallback, useEffect, useState } from 'react';
import { get, post } from '../api';
import { useData } from '../App';
import { addDays, cx, fmtHM, fmtShort, iso, locOf, noWorkReason, pad, POINTS, rangeLbl, slotLabel, SLOTS, SLOT_MIN, SLOT_START, slotTime, STEP, TI_ROOM, todayIso } from '../lib';
import { Empty, Loading, useFail, useToast } from '../ui';
import { PeoplePicker } from '../PeoplePicker';
import KeyAdmView from './KeyAdmView';

interface Log { Id: number; KeyId: string; KeyPointId: string; BookingId: number | null; ExtraKey: string | null; OutAt: string; OutBy: string; DueAt: string | null; InAt: string | null; InBy: string | null }
interface Row { bookingId: number; roomId: string; extra: string | null; extraName: string | null; start: number; end: number; who: string; log: Log | null }
interface KeysDay {
  date: string; points: string[]; rows: Row[];
  loose: { id: string; name: string; point: string; local: string | null; note: string | null; open: Log | null }[];
  openRoomKeys: { roomId: string; who: string; logId: number }[];
  moves: Log[];
}
type Form = { kind: 'out'; bookingId: number; extra: string | null; who: string } | { kind: 'in'; logId: number; who: string } | { kind: 'outk'; keyId: string; who: string; due: number };
type St = 'aguardando' | 'retirada' | 'atrasada' | 'devolvida' | 'nao' | 'prevista';
const KST: Record<St, string> = { aguardando: 'Aguardando retirada', retirada: 'Retirada', atrasada: 'Devolução atrasada', devolvida: 'Devolvida', nao: 'Não retirada', prevista: 'Prevista' };
const rowKey = (x: Row) => `${x.bookingId}:${x.extra ?? ''}`;

/**
 * Chaves: a agenda do dia segue as reservas; quem reservou já vem preenchido na retirada.
 * As Salas Microsoft podem ter chaves extras (charge e armários), cada uma numa linha.
 * Filtros por horário, local, responsável e situação; visualização em lista ou grade do dia.
 */
/** Chaves tem duas abas: a agenda do dia e a Gestão de chaves (cadastro das chaves avulsas do ponto) */
export default function KeysView() {
  const [tab, setTab] = useState<'agenda' | 'gestao'>('agenda');
  const tabs = (
    <div className="seg mgtabs ktabs" role="group" aria-label="Chaves">
      <button type="button" aria-pressed={tab === 'agenda'} onClick={() => setTab('agenda')}>Agenda de chaves</button>
      <button type="button" aria-pressed={tab === 'gestao'} onClick={() => setTab('gestao')}>Gestão de chaves</button>
    </div>
  );
  return tab === 'gestao' ? <KeyAdmView tabs={tabs} /> : <KeysAgenda tabs={tabs} />;
}

function KeysAgenda({ tabs }: { tabs: JSX.Element }) {
  const { me, roomById, catalog, events } = useData();
  const toast = useToast(), fail = useFail();
  const [date, setDate] = useState(todayIso());
  const [point, setPoint] = useState('');
  const [data, setData] = useState<KeysDay | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [mode, setMode] = useState<'lista' | 'grade'>('lista');
  const [f, setF] = useState({ time: '', room: '', who: '', status: '' });
  const load = useCallback(() => {
    get<KeysDay>(`keys?date=${date}${point ? '&point=' + point : ''}`).then(setData).catch(fail);
  }, [date, point, fail]);
  useEffect(() => { setData(null); setForm(null); load(); }, [load]);

  const isToday = date === todayIso(), n = Date.now();
  const save = async () => {
    if (!form) return;
    if (!form.who.trim()) { toast('Informe o nome da pessoa.'); return; }
    try {
      if (form.kind === 'out') await post('keys/out', { bookingId: form.bookingId, extraKey: form.extra ?? undefined, by: form.who.trim() });
      else if (form.kind === 'in') await post(`keys/${form.logId}/in`, { by: form.who.trim() });
      else await post('keys/out', { looseKeyId: form.keyId, by: form.who.trim(), dueSlot: form.due });
      toast(form.kind === 'in' ? `Devolução registrada, por ${form.who.trim()}.` : `Retirada registrada com ${form.who.trim()}.`);
      setForm(null); load();
    } catch (err) { fail(err); }
  };
  const kForm = (label: string, extra: JSX.Element | null = null, hint = '') => form && (
    <div className="kform">
      <div className="field"><label htmlFor="kWho">{label}</label>
        {form.who ? <div className="person"><span className="nm">{form.who}</span><button type="button" className="x" aria-label="Trocar a pessoa" onClick={() => setForm({ ...form, who: '' } as Form)}>×</button></div>
          : <PeoplePicker id="kWho" autoFocus allowText placeholder="Busque o nome ou digite e clique em Usar" buttonLabel="Usar" onPick={p => setForm({ ...form, who: p.name } as Form)} />}</div>
      {extra}
      <button className="btn sm" onClick={save}>Confirmar</button><button className="btn ghost sm" onClick={() => setForm(null)}>Cancelar</button>
      {hint && <span className="hint">{hint}</span>}
    </div>
  );

  if (!data) return <section className="view">{tabs}<Loading /></section>;
  const status = (x: Row): St => {
    const l = x.log, endT = slotTime(date, x.end).getTime();
    if (l?.InAt) return 'devolvida';
    if (l) return n > endT ? 'atrasada' : 'retirada';
    if (!isToday) return date > todayIso() ? 'prevista' : 'nao';
    return n > endT ? 'nao' : 'aguardando';
  };
  const label = (x: Row) => { const r = roomById(x.roomId)!; return x.extra ? `${r.name} · ${x.extraName}` : r.name; };
  const extrasOf = (x: Row) => data.rows.filter(y => y.bookingId === x.bookingId && y.extra).length;
  const stRows = data.rows.map(x => ({ x, st: status(x) }));
  const shown = stRows.filter(({ x, st }) => (f.time === '' || (x.start <= +f.time && x.end > +f.time)) && (!f.room || x.roomId === f.room)
    && (!f.who || [x.who, x.log?.OutBy, x.log?.InBy].some(w => w && w.toLowerCase().includes(f.who.toLowerCase())))
    && (!f.status || f.status === st || (f.status === 'retirada' && st === 'atrasada') || (f.status === 'aguardando' && st === 'prevista')));
  const anyF = f.time !== '' || f.room || f.who || f.status;
  const openNow = data.moves.filter(l => !l.InAt);
  const late = openNow.filter(l => l.DueAt && new Date(l.DueAt).getTime() < n);
  const pending = isToday ? stRows.filter(({ st }) => st === 'aguardando').length : data.rows.length;
  const dues = Array.from({ length: SLOTS / STEP }, (_, k) => (k + 1) * STEP).filter(i => slotTime(todayIso(), i).getTime() > n);
  const keyName = (id: string) => {
    const [roomId, code] = id.split(':'), r = roomById(roomId);
    if (r) return code ? `${r.name} · ${r.extraKeys.find(k => k.code === code)?.name ?? code}` : r.name;
    return data.loose.find(k => k.id === id)?.name ?? id;
  };
  const moves = data.moves.flatMap(l => [{ t: l.OutAt, txt: `Retirada · ${keyName(l.KeyId)} · ${l.OutBy}` }, ...(l.InAt ? [{ t: l.InAt, txt: `Devolução · ${keyName(l.KeyId)} · ${l.InBy ?? l.OutBy}` }] : [])])
    .filter(m => iso(new Date(m.t)) === date).sort((a, b) => b.t.localeCompare(a.t));
  const nw = noWorkReason(events, date);
  const roomOpts = [...new Map(data.rows.map(x => [x.roomId, roomById(x.roomId)!.name])).entries()];
  const pts = point ? [point] : data.points;
  const gridRooms = catalog.rooms.filter(r => r.id !== TI_ROOM && pts.includes(r.keyPoint) && (!f.room || r.id === f.room));
  const m = (() => { const d = new Date(); return d.getHours() * 60 + d.getMinutes() - SLOT_START; })();

  const rowEl = ({ x, st }: { x: Row; st: St }) => {
    const r = roomById(x.roomId)!, l = x.log;
    let pill: JSX.Element, act: JSX.Element | null = null, fe: JSX.Element | null = null, cls = 'krow';
    if (st === 'devolvida') pill = <span className="pill free">Devolvida às {fmtHM(new Date(l!.InAt!))}{l!.InBy && l!.InBy !== l!.OutBy ? ' por ' + l!.InBy : ''}</span>;
    else if (st === 'retirada' || st === 'atrasada') {
      if (st === 'atrasada') cls += ' late';
      pill = <span className={cx('pill', st === 'atrasada' ? 'busy' : 'warn')}>{KST[st]} · {fmtHM(new Date(l!.OutAt))} · {l!.OutBy}</span>;
      act = <button className="btn sm" onClick={() => setForm({ kind: 'in', logId: l!.Id, who: l!.OutBy })}>Registrar devolução</button>;
      if (form?.kind === 'in' && form.logId === l!.Id) fe = kForm('Devolvido por', null, 'Preenchido com quem retirou. Altere se outra pessoa estiver devolvendo.');
    } else if (st === 'prevista' || st === 'nao') pill = <span className="pill neutral">{KST[st]}</span>;
    else {
      const holder = !x.extra && data.openRoomKeys.find(k => k.roomId === r.id);
      if (holder) pill = <span className="pill warn">Chave ainda com {holder.who}</span>;
      else {
        pill = <span className="pill neutral">Aguardando retirada</span>;
        act = <button className="btn ghost sm" onClick={() => setForm({ kind: 'out', bookingId: x.bookingId, extra: x.extra, who: x.who })}>Registrar retirada</button>;
        if (form?.kind === 'out' && form.bookingId === x.bookingId && form.extra === x.extra) fe = kForm('Retirado por', null, 'Preenchido com quem reservou. Altere se outra pessoa estiver retirando.');
      }
    }
    const nx = extrasOf(x);
    return (
      <div className={cls} key={rowKey(x)}>
        <span className="tm">{rangeLbl(x.start, x.end - 1)}</span>
        <span className="rm"><b>{label(x)}</b><small>{x.extra ? 'Chave extra da reserva' : locOf(r)}{!x.extra && nx ? ` · + ${nx} chave${nx > 1 ? 's' : ''} extra${nx > 1 ? 's' : ''}` : ''}</small></span>
        <span className="who">{x.who}<small>Retira na {POINTS[r.keyPoint]}</small></span>
        <span className="st">{pill}{!fe && act}</span>{fe}
      </div>
    );
  };

  return (
    <section className="view">
      {tabs}
      <div className="vhead">
        <div><span className="eyebrow">Controle de chaves</span><h2>{me.keyPoints.length > 1 ? 'Todos os pontos de retirada' : POINTS[me.keyPoints[0]]}</h2>
          <p>As chaves das salas seguem a agenda de reservas: quem reservou aparece aqui no horário certo e já vem preenchido na retirada. As Salas Microsoft podem ter chaves extras (charge e armários). As outras chaves do colégio têm registro avulso, com prazo de devolução.</p></div>
        <div className="ctrl">
          {me.keyPoints.length > 1 && (
            <div className="field"><label htmlFor="kPoint">Ponto de retirada</label>
              <select id="kPoint" value={point} onChange={e => setPoint(e.target.value)}><option value="">Todos</option>{me.keyPoints.map(p => <option key={p} value={p}>{POINTS[p]}</option>)}</select></div>
          )}
          <div className="daynav"><button className="icon-btn" aria-label="Dia anterior" onClick={() => setDate(addDays(date, -1))}>‹</button>
            <strong>{isToday ? 'Hoje · ' : ''}{fmtShort(date)}</strong>
            <button className="icon-btn" aria-label="Próximo dia" onClick={() => setDate(addDays(date, 1))}>›</button></div>
        </div>
      </div>
      <div className="tiles">
        <div className="tile"><b>{pending}</b><span>{isToday ? 'retiradas previstas ainda hoje' : 'retiradas previstas no dia'}</span></div>
        <div className="tile"><b>{openNow.length}</b><span>chaves fora agora</span></div>
        <div className={cx('tile', late.length > 0 && 'alert')}><b>{late.length}</b><span>devoluções atrasadas</span></div>
      </div>
      <div className="kgrid">
        <section className="panel"><div className="panel-h"><h2>Chaves das salas · agenda do dia</h2>
          <span className="sub">{nw ? 'Sem expediente: ' + nw : isToday ? 'Registre a retirada quando a pessoa buscar a chave e a devolução quando trouxer de volta.' : 'Somente visualização. Os registros são feitos no dia.'}</span>
          <div className="seg" role="group" aria-label="Forma de visualização" style={{ marginTop: 6, width: 'fit-content' }}>
            <button type="button" aria-pressed={mode === 'lista'} onClick={() => setMode('lista')}>Lista</button>
            <button type="button" aria-pressed={mode === 'grade'} onClick={() => setMode('grade')}>Grade do dia</button></div></div>
          <div className="kfilters">
            <div className="field"><label htmlFor="kfTime">Horário</label><select id="kfTime" value={f.time} onChange={e => setF({ ...f, time: e.target.value })}><option value="">Todos</option>{Array.from({ length: SLOTS / STEP }, (_, k) => k * STEP).map(i => <option key={i} value={i}>{slotLabel(i)}</option>)}</select></div>
            <div className="field"><label htmlFor="kfRoom">Local</label><select id="kfRoom" value={f.room} onChange={e => setF({ ...f, room: e.target.value })}><option value="">Todos</option>{roomOpts.map(([id, nm]) => <option key={id} value={id}>{nm}</option>)}</select></div>
            <div className="field"><label htmlFor="kfWho">Responsável</label><input id="kfWho" value={f.who} placeholder="Nome" onChange={e => setF({ ...f, who: e.target.value })} /></div>
            <div className="field"><label htmlFor="kfSt">Situação</label><select id="kfSt" value={f.status} onChange={e => setF({ ...f, status: e.target.value })}><option value="">Todas</option>
              {(['aguardando', 'retirada', 'devolvida', 'nao'] as St[]).map(v => <option key={v} value={v}>{KST[v]}</option>)}</select></div>
            {anyF && <button type="button" className="link-btn muted" onClick={() => setF({ time: '', room: '', who: '', status: '' })}>Limpar filtros</button>}
            <span className="hint">{shown.length} de {data.rows.length} chave{data.rows.length === 1 ? '' : 's'}</span>
          </div>
          {mode === 'grade' ? (
            <div className="rbody">
              <div className="dgrid"><div className="dg-in">
                <div className="dg-row"><div className="dg-room"><b>Sala</b></div><div className="dg-track dg-hours">{Array.from({ length: 12 }, (_, h) => <span key={h}>{pad(7 + h)}h</span>)}</div></div>
                {!gridRooms.length && <div className="empty">Nenhuma sala para mostrar.</div>}
                {gridRooms.map(r => (
                  <div className="dg-row" key={r.id}>
                    <div className="dg-room"><b>{r.name}</b><small>{POINTS[r.keyPoint]}</small></div>
                    <div className="dg-track">
                      {isToday && m >= 0 && m < SLOTS * SLOT_MIN && <i className="dg-now" style={{ left: `${(m / (SLOTS * SLOT_MIN) * 100).toFixed(2)}%` }} />}
                      {shown.filter(({ x }) => !x.extra && x.roomId === r.id).map(({ x, st }) => {
                        const nx = extrasOf(x);
                        return <button type="button" key={rowKey(x)} className={cx('dg-ev', 'st-' + st)} style={{ gridColumn: `${x.start + 1}/${x.end + 1}` }}
                          title={`${rangeLbl(x.start, x.end - 1)} · ${x.who}`} onClick={() => { setMode('lista'); setF({ ...f, room: r.id }); }}>
                          {x.who}<small>{KST[st]}{nx ? ` · +${nx} chave${nx > 1 ? 's' : ''}` : ''}</small></button>;
                      })}
                    </div>
                  </div>
                ))}
              </div></div>
              <div className="glegend"><span><i className="sw" style={{ background: 'var(--surface-2)' }} />Aguardando retirada</span><span><i className="sw" style={{ background: 'var(--warn)' }} />Retirada</span>
                <span><i className="sw" style={{ background: 'var(--busy)' }} />Devolução atrasada</span><span><i className="sw" style={{ background: 'var(--free)' }} />Devolvida</span>
                <span>Clique numa reserva para registrar a retirada ou a devolução</span></div>
            </div>
          ) : (
            <div className="klist">
              {shown.length ? shown.map(rowEl) : <Empty>{data.rows.length ? 'Nenhuma chave com esses filtros.' : 'Nenhuma reserva neste dia para as salas deste ponto.'}</Empty>}
            </div>
          )}</section>
        <div className="side">
          <section className="panel"><div className="panel-h"><h2>Outras chaves</h2><span className="sub">Locais sem reserva no app · cadastradas em "Gestão de chaves"</span></div>
            <div className="klist">
              {!data.loose.length && <Empty>Nenhuma chave avulsa neste ponto.</Empty>}
              {data.loose.map(k => {
                const l = k.open;
                const isLate = !!l?.DueAt && new Date(l.DueAt).getTime() < n;
                let fe: JSX.Element | null = null;
                if (form?.kind === 'in' && l && form.logId === l.Id) fe = kForm('Devolvido por');
                if (form?.kind === 'outk' && form.keyId === k.id) fe = kForm('Retirado por',
                  <div className="field"><label htmlFor="kDue">Devolver até</label>
                    <select id="kDue" value={form.due} onChange={e => setForm({ ...form, due: +e.target.value })}>
                      {dues.length ? dues.map(i => <option key={i} value={i}>{slotLabel(i)}</option>) : <option value={SLOTS}>Fim do dia</option>}</select></div>);
                return (
                  <div className={cx('krow compact', isLate && 'late')} key={k.id}>
                    <span className="rm"><b>{k.name}</b><small>{POINTS[k.point]}{k.local ? ' · ' + k.local : ''}</small></span>
                    <span className="st">
                      {l ? <span className={cx('pill', isLate ? 'busy' : 'warn')}>Com {l.OutBy}{l.DueAt ? ` · devolver até ${fmtHM(new Date(l.DueAt))}` : ''}</span> : <span className="pill free">Disponível</span>}
                      {!fe && (l ? <button className="btn sm" onClick={() => setForm({ kind: 'in', logId: l.Id, who: l.OutBy })}>Registrar devolução</button>
                        : isToday && <button className="btn ghost sm" onClick={() => setForm({ kind: 'outk', keyId: k.id, who: '', due: dues[0] ?? SLOTS })}>Registrar retirada</button>)}
                    </span>{fe}
                  </div>
                );
              })}
            </div></section>
          <section className="panel"><div className="panel-h"><h2>Movimentações do dia</h2></div>
            <div className="log">{moves.length ? moves.map((mv, k) => <div key={k}><span className="tm">{fmtHM(new Date(mv.t))}</span><span>{mv.txt}</span></div>) : <span className="mine-empty">Nada registrado ainda.</span>}</div></section>
        </div>
      </div>
    </section>
  );
}
